'use client';

import { useMemo, useState } from 'react';

import Image from 'next/image';

import { formatPrice } from '@/lib/price';
import { cn, Input } from '@holistic/components';
import {
  Check,
  ImageOff,
  Microscope,
  PhoneCall,
  Search,
} from '@holistic/components/icons';

import type { TalepRequestType } from './types';

import { SEARCH_THRESHOLD, STEP_CONTENT } from './constants';
import StepActions from './step-actions';
import StepHeading from './step-heading';

/**
 * Fiyatı belli olan kalem. Fiyatsız ya da analiz isteyen kalem tutar taahhüt
 * etmez; kartında fiyat yerine "Analiz gerekli" rozeti çıkar.
 */
const isQuoted = (o: TalepRequestType) =>
  o.price !== null && !o.isAnalysisRequired;

type RequestTypeStepProps = {
  options: TalepRequestType[];
  /** Seçim üst bileşende tutulur; yan panel de aynı listeyi gösterir. */
  selectedIds: string[];
  onToggle: (id: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  stepNumber: number;
  onSelectAnalysisType?: (type: TalepRequestType) => void;
};

/**
 * Arıza seçimi. Tek cihaz için birden fazla arıza seçilebilir — backend her
 * seçim için ayrı bir kalem açıp fiyatını o an kilitler.
 *
 * Seçim URL'e yazılmaz; "Seçilenlerle devam et" ile tek seferde
 * `type=id1,id2` olarak gönderilir. Aksi halde her tıklama bir router.push
 * doğurur ve sunucu adımı yeniden çözerdi.
 */
const RequestTypeStep = ({
  options,
  selectedIds,
  onToggle,
  onSubmit,
  onBack,
  stepNumber,
  onSelectAnalysisType,
}: RequestTypeStepProps) => {
  const [term, setTerm] = useState('');

  const showSearch = options.length >= SEARCH_THRESHOLD;

  const visible = useMemo(() => {
    const q = term.trim().toLocaleLowerCase('tr');
    if (!q) return options;
    return options.filter((o) =>
      `${o.name} ${o.description ?? ''}`.toLocaleLowerCase('tr').includes(q)
    );
  }, [options, term]);

  // Cihaz için tanımlı arıza yoksa seçilecek bir şey de yok.
  if (options.length === 0) {
    return (
      <div className="space-y-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-xs md:p-8">
        <StepHeading
          stepNumber={stepNumber}
          title={STEP_CONTENT.type.title}
          description={STEP_CONTENT.type.description}
        />
        <p className="rounded-xl bg-gray-50 p-5 text-sm leading-relaxed text-gray-500">
          Bu cihaz için şu an tanımlı bir arıza tipi bulunmuyor. Başka bir model
          seçebilir ya da bizimle iletişime geçebilirsiniz.
        </p>
        <StepActions
          onBack={onBack}
          onContinue={onSubmit}
          continueLabel="Seçilenlerle devam et"
          disabled
        />
      </div>
    );
  }

  const chosen = options.filter((o) => selectedIds.includes(o.id));

  // Analiz gerektiren bir arıza seçildiyse talep peşin tahsilata girmez; bu
  // yüzden fiyatlı arızalarla birlikte gönderilemez. İki grup birbirini kilitler.
  // Analiz gerektiren arızadan da talep başına yalnızca bir tane seçilebilir;
  // biri seçilince diğer analiz kartları da kilitlenir.
  // Kilit hesabı filtrelenmiş değil tam listeden yapılır: arama sonucu daralsa
  // da seçimler ve kilitler değişmez.
  const hasAnalysisChoice = chosen.some((o) => !isQuoted(o));
  const hasQuotedChoice = chosen.some(isQuoted);

  return (
    <div className="space-y-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-xs md:p-8">
      <StepHeading
        stepNumber={stepNumber}
        title={STEP_CONTENT.type.title}
        description={STEP_CONTENT.type.description}
      />

      {(hasAnalysisChoice || hasQuotedChoice) && (
        <p className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3 text-xs leading-relaxed text-amber-800">
          {hasAnalysisChoice
            ? 'Analiz gerektiren bir arıza seçtiniz. Tutar inceleme sonrasında netleşeceği için aynı talepte başka bir arıza seçemezsiniz; diğer arızalar için ayrı talep oluşturabilirsiniz.'
            : 'Fiyatlı arıza seçtiniz. Analiz gerektiren arızalar için ayrı talep oluşturmanız gerekir.'}
        </p>
      )}

      {showSearch && (
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={STEP_CONTENT.type.searchPlaceholder}
            className="h-11 rounded-xl pl-10"
          />
        </div>
      )}

      {visible.length === 0 ? (
        <p className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5 text-sm text-gray-500">
          Aramanızla eşleşen bir arıza yok.
        </p>
      ) : (
        /* Dar ekranda liste uzayınca kart sayfayı yutuyordu; grid kendi içinde
           kayar, md'den itibaren tam yükseklikte açılır. `px-1` odak halkası
           kırpılmasın diye. Kaydırma zinciri bilerek engellenmiyor: liste
           ucuna gelince sayfa kaydırmaya devam etsin, parmak listenin
           üzerindeyken sayfa kilitlenmesin. */
        <div
          role="group"
          className="-mx-1 grid max-h-[26rem] grid-cols-2 gap-4 overflow-y-auto px-1 py-1 sm:grid-cols-3 md:max-h-none md:overflow-visible lg:grid-cols-4"
        >
          {visible.map((o) => {
            const isSelected = selectedIds.includes(o.id);
            const quoted = isQuoted(o);
            // Seçilmiş bir analiz arızası varsa geri kalan her kart kilitlenir;
            // fiyatlı seçim varsa yalnızca analiz kartları. Seçili kart hep
            // tıklanabilir kalır ki kullanıcı seçimini geri alabilsin.
            const isLocked =
              !isSelected &&
              (hasAnalysisChoice || (!quoted && hasQuotedChoice));

            return (
              <button
                key={o.id}
                type="button"
                role="checkbox"
                aria-checked={isSelected}
                disabled={isLocked}
                title={
                  isLocked
                    ? 'Analiz gerektiren ve fiyatlı arızalar aynı talepte seçilemez.'
                    : undefined
                }
                onClick={() => {
                  if (!quoted) {
                    onSelectAnalysisType?.(o);
                  } else {
                    onToggle(o.id);
                  }
                }}
                className={cn(
                  'group flex flex-col overflow-hidden rounded-2xl border text-left transition-all duration-200',
                  'focus-visible:ring-primary/30 focus-visible:ring-2 focus-visible:outline-none',
                  isLocked && 'cursor-not-allowed opacity-45',
                  !isLocked && 'cursor-pointer',
                  isSelected
                    ? 'border-primary ring-primary/60 bg-primary/[0.03] ring-1'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                )}
              >
                <span className="relative flex h-28 items-center justify-center bg-gray-50/70 p-3">
                  {o.imageUrl ? (
                    <Image
                      src={o.imageUrl}
                      alt={o.name}
                      width={200}
                      height={140}
                      className="h-full w-auto object-contain"
                    />
                  ) : (
                    <ImageOff className="size-5 text-gray-300" />
                  )}

                  <span
                    className={cn(
                      'absolute top-2.5 right-2.5 flex size-6 items-center justify-center rounded-full border transition-colors',
                      !quoted
                        ? 'border-amber-200 bg-amber-50 text-amber-700'
                        : isSelected
                          ? 'border-primary bg-primary text-white'
                          : 'border-gray-200 bg-white'
                    )}
                  >
                    {!quoted ? (
                      <PhoneCall className="size-3" />
                    ) : (
                      isSelected && (
                        <Check className="size-3.5" strokeWidth={3} />
                      )
                    )}
                  </span>
                </span>

                <span className="flex flex-1 flex-col gap-1.5 p-4">
                  <span className="text-sm font-semibold text-gray-900">
                    {o.name}
                  </span>
                  {o.description && (
                    <span className="line-clamp-2 text-xs leading-relaxed text-gray-500">
                      {o.description}
                    </span>
                  )}
                  <span className="mt-auto pt-1">
                    {quoted ? (
                      /* Tutar tek başına kesin fiyat gibi okunmasın diye her
                       kartta "ön teklif" ibaresi fiyatın altında duruyor. */
                      <span className="flex flex-col">
                        <span className="text-primary text-sm font-semibold">
                          {formatPrice(o.price!)}
                        </span>
                        <span className="text-[11px] font-medium text-gray-400">
                          ön teklif
                        </span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                        <PhoneCall className="size-3" />
                        Biz Sizi Arayalım
                      </span>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <StepActions
        onBack={onBack}
        onContinue={onSubmit}
        continueLabel="Seçilenlerle devam et"
        disabled={chosen.length === 0}
      />
    </div>
  );
};

export default RequestTypeStep;
